import type { Application } from "@ark/contracts";

const bundledRemotePaths = {
  ai: "/remotes/ai/remoteEntry.js",
  docs: "/remotes/docs/remoteEntry.js",
  drive: "/remotes/drive/remoteEntry.js",
  gallery: "/remotes/gallery/remoteEntry.js",
  im: "/remotes/im/remoteEntry.js",
  meeting: "/remotes/meeting/remoteEntry.js",
} as const;

export type BundledDesktopApp = keyof typeof bundledRemotePaths;

export const desktopMenuSections = [
  "home",
  "collaboration",
  "workspace",
  "directory",
] as const;

export function isDesktopRuntime(): boolean {
  return (
    import.meta.env.VITE_RUNTIME === "desktop" ||
    import.meta.env.VITE_RUNTIME === "desktop-dev"
  );
}

export function hasNativeDesktopBridge(): boolean {
  return (
    typeof window !== "undefined" &&
    Object.hasOwn(window, "__TAURI_INTERNALS__")
  );
}

export function isBundledDesktopApplication(
  app: Pick<Application, "slug" | "version">,
): app is Pick<Application, "slug" | "version"> & {
  slug: BundledDesktopApp;
} {
  return (
    app.version.trust === "trusted_inner" &&
    Object.hasOwn(bundledRemotePaths, app.slug)
  );
}

export function resolveApplicationRemoteEntry(
  app: Pick<Application, "slug" | "version">,
  configuredEntry: string,
): string | undefined {
  if (!isDesktopRuntime()) return configuredEntry;
  if (!isBundledDesktopApplication(app)) return undefined;
  return import.meta.env.VITE_RUNTIME === "desktop"
    ? bundledRemotePaths[app.slug]
    : configuredEntry;
}

export function desktopAppOrder(slug: string): number {
  return (
    {
      im: 0,
      docs: 10,
      drive: 20,
      gallery: 25,
      meeting: 30,
      ai: 40,
    }[slug] ?? 100
  );
}

export function desktopHomeRoute(search = ""): string {
  return `/home${search}`;
}

export function isAllowedExternalURL(rawURL: string): boolean {
  try {
    const url = new URL(rawURL);
    if (url.username || url.password) return false;
    if (url.protocol === "https:") return true;
    return (
      import.meta.env.DEV &&
      url.protocol === "http:" &&
      (url.hostname === "localhost" || url.hostname === "127.0.0.1")
    );
  } catch {
    return false;
  }
}

export async function openExternalURL(rawURL: string): Promise<void> {
  if (!isAllowedExternalURL(rawURL)) {
    throw new Error("桌面端只能打开已登记的 HTTPS 外部地址");
  }
  if (isDesktopRuntime() && hasNativeDesktopBridge()) {
    const { openUrl } = await import("@tauri-apps/plugin-opener");
    await openUrl(rawURL);
    return;
  }
  window.open(rawURL, "_blank", "noopener,noreferrer");
}

export function desktopRouteFromURL(rawURL: string): string | undefined {
  if (rawURL.length > 2048) return undefined;
  try {
    const url = new URL(rawURL);
    if (url.protocol !== "ark:") return undefined;
    const segments = url.pathname.split("/").filter(Boolean);
    const appSlug = segments[0];
    if (
      url.hostname === "open" &&
      appSlug &&
      Object.hasOwn(bundledRemotePaths, appSlug)
    ) {
      return `/apps/${segments.map(encodeURIComponent).join("/")}${url.search}`;
    }
    if (
      url.hostname === "join" &&
      segments.length === 2 &&
      segments[0] === "meeting" &&
      segments[1] &&
      segments[1].length <= 256
    ) {
      return `/join/meeting/${encodeURIComponent(segments[1])}`;
    }
    return undefined;
  } catch {
    return undefined;
  }
}
